import React, { useEffect, useId, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { PhoneCall } from 'lucide-react';
import DatePicker from '../../components/DatePicker/DatePicker';
import TimePicker from '../../components/TimePicker/TimePicker';
import Dropdown from '../../components/Dropdown/Dropdown';
import { logLeadCall } from '../../api/leads';
import { useI18n } from '../../i18n/I18nContext';
import { errorText } from '../../utils/errorText';
import { todayYmd } from '../../utils/datetime';
import { CALL_OUTCOMES, EDITABLE_STATUSES, OUTCOME_SUGGESTS, dayFromToday, toInstant } from './leadUi';

const QUICK_DAYS = [
  { days: 1, key: 'tomorrow' },
  { days: 3, key: 'inThreeDays' },
  { days: 7, key: 'nextWeek' },
];

const EMPTY = { outcome: '', note: '', status: '', statusTouched: false, plan: true, date: '', time: '10:00' };

/**
 * "Qo'ng'iroqni yozish": what the call ended with, a note, when to call next and, if it changed,
 * the lead's status. One call is one entry in the lead's history; the next call lands in the
 * owner's "My tasks". A "not interested" or "wrong number" outcome suggests "Lost" and no further
 * call; the person can still pick otherwise.
 *
 *   <CallLogForm leadId={id} status={lead.status} onSaved={(data) => setDetail(data)} />
 */
export default function CallLogForm({ leadId, status, onSaved }) {
  const { t } = useI18n();
  const uid = useId();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);
  // Set on every mount: StrictMode mounts twice in dev, and a ref left false would drop onSaved.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const pickOutcome = (outcome) => {
    setError('');
    setForm((prev) => {
      const suggested = OUTCOME_SUGGESTS[outcome];
      const next = { ...prev, outcome };
      if (!prev.statusTouched) next.status = suggested || '';
      // A lost lead needs no further call; any other outcome plans one again.
      next.plan = (prev.statusTouched ? prev.status : next.status) !== 'LOST';
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.outcome) {
      setError(t('leads.call.outcomeRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const data = await logLeadCall(leadId, {
        outcome: form.outcome,
        note: form.note.trim() || undefined,
        nextCallAt: form.plan ? toInstant(form.date, form.time) : null,
        status: form.status && form.status !== status ? form.status : undefined,
      });
      if (!alive.current) return;
      setForm(EMPTY);
      toast.success(t('leads.call.saved'));
      onSaved(data);
    } catch (err) {
      if (alive.current) setError(errorText(err, t('leads.call.saveError')));
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  const statusOptions = [
    { value: '', label: t('leads.call.keepStatus') },
    ...EDITABLE_STATUSES.map((s) => ({ value: s, label: t(`leads.status.${s}`) })),
  ];

  return (
    <form className="section-card lead-call" onSubmit={submit} noValidate>
      <div className="lead-card-head">
        <span className="ui-icon-chip ui-icon-chip--md" data-tone="brand" aria-hidden="true"><PhoneCall /></span>
        <h2 className="lead-card-title">{t('leads.call.title')}</h2>
      </div>

      <fieldset className="lead-fieldset">
        <legend className="ui-label">{t('leads.call.outcome')}</legend>
        <div className="ui-chip-row ui-chip-row--wrap lead-outcomes">
          {CALL_OUTCOMES.map((o) => (
            <button
              key={o}
              type="button"
              className={`ui-chip lead-outcome lead-outcome--${o.toLowerCase()}`}
              aria-pressed={form.outcome === o}
              onClick={() => pickOutcome(o)}
            >
              {t(`leads.outcome.${o}`)}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="ui-field">
        <label className="ui-label" htmlFor={`${uid}-note`}>{t('leads.call.note')}</label>
        <textarea
          id={`${uid}-note`}
          rows={2}
          maxLength={2000}
          value={form.note}
          placeholder={t('leads.call.notePlaceholder')}
          onChange={(e) => set({ note: e.target.value })}
        />
      </div>

      <div className="lead-call-grid">
        <div className="ui-field">
          <span className="ui-label">{t('leads.call.nextCall')}</span>
          <div className="ui-chip-row ui-chip-row--wrap">
            {QUICK_DAYS.map((q) => {
              const ymd = dayFromToday(q.days);
              return (
                <button
                  key={q.key}
                  type="button"
                  className="ui-chip"
                  aria-pressed={form.plan && form.date === ymd}
                  onClick={() => set({ plan: true, date: ymd })}
                >
                  {t(`leads.call.${q.key}`)}
                </button>
              );
            })}
            <button type="button" className="ui-chip" aria-pressed={!form.plan} onClick={() => set({ plan: !form.plan })}>
              {t('leads.call.noNextCall')}
            </button>
          </div>
          {form.plan && (
            <div className="ui-picker-pair">
              <DatePicker
                value={form.date}
                onChange={(v) => set({ date: v })}
                min={todayYmd()}
                aria-label={`${t('leads.call.nextCall')}: ${t('leads.form.date')}`}
              />
              <TimePicker
                value={form.time}
                onChange={(v) => set({ time: v || '10:00' })}
                aria-label={`${t('leads.call.nextCall')}: ${t('leads.form.time')}`}
              />
            </div>
          )}
        </div>

        <div className="ui-field">
          <span className="ui-label" id={`${uid}-status`}>{t('leads.call.status')}</span>
          <Dropdown
            value={form.status}
            onChange={(v) => set({ status: v, statusTouched: true, plan: v === 'LOST' ? false : form.plan })}
            options={statusOptions}
            labelledBy={`${uid}-status`}
          />
        </div>
      </div>

      {error && <p className="ui-error" role="alert">{error}</p>}

      <div className="lead-call-actions">
        <button type="submit" className="ds-btn ds-btn-primary" disabled={saving}>{t('leads.call.save')}</button>
      </div>
    </form>
  );
}
